import { useEffect, useRef, useState } from 'react';
import { withIcon } from './Icon';
import * as Dialog from '@radix-ui/react-dialog';
import Button from './Button';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';

const LoadingIcon = withIcon(faSpinnerThird);
const ProgressModal = ({
    isOpen,
    onClose,
    hideClose,
    title,
    children,
    progress = 0,
    onConfirm,
    loading,
    confirmText = 'Confirm',
    confirmIcon = null,
    titleIcon = null,
    confirmDisabled,
    showProgress = true,
    className = '',
    containerRef = null,
}) => {
    const [hasScroll, setHasScroll] = useState(false);
    const defaultContainerRef = useRef(document.getElementById('modal-root'));
    const modalContainerRef = containerRef || defaultContainerRef;
    const contentContainerRef = useRef(null);

    const handleOpenChange = (open) => {
        if (!open) onClose?.();
    };

    const handleWrapperClick = (event) => {
        if (event.target instanceof Element && !event.target.closest('.js-modal')) {
            onClose?.();
        }
    };

    useEffect(() => {
        const el = contentContainerRef.current;
        if (el) {
            const checkScroll = () => {
                const scroll = el.scrollHeight > el.clientHeight;
                setHasScroll(scroll);
            };

            checkScroll();

            const resizeObserver = new ResizeObserver(checkScroll);
            resizeObserver.observe(el);

            return () => resizeObserver.disconnect();
        }
    }, [children]);

    if (!modalContainerRef?.current) return null;

    const containerElement = modalContainerRef?.current || defaultContainerRef.current;

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Portal container={containerElement}>
                <Dialog.Overlay className="absolute inset-0 bg-[rgba(0,0,0,0.75)] overflow-auto flex flex-col justify-start items-center z-[21000]" />

                <Dialog.Content
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    className="flex justify-center items-center absolute top-0 left-0 w-full h-full py-14 pointer-events-auto js-modal"
                    onClick={handleWrapperClick}
                >
                    <div
                        className={`flex flex-col w-full max-w-[calc(100vw-1rem)] sm:w-[536px] flex-none h-min max-h-[calc(100dvh-7rem)] bg-surface-weak rounded-xl z-[21000] ${className}`}
                    >
                        {/* Header */}
                        {title && (
                            <div className="flex justify-between items-center gap-8 px-6 flex-none min-h-14">
                                <Dialog.Title className="text-base font-semibold text-content-strong max-w-[32rem]">
                                    {titleIcon} {title}
                                </Dialog.Title>

                                {!hideClose && (
                                    <div className="flex">
                                        <Dialog.Close asChild>
                                            <button
                                                className="w-8 h-8 -mr-4 p-0 flex items-center justify-center text-content-strong hover:bg-gray-100 rounded transition-colors"
                                                onClick={onClose}
                                                tabIndex={-1}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path
                                                        d="M15 5L5 15M5 5L15 15"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </button>
                                        </Dialog.Close>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Progress Bar */}
                        {showProgress && title && (
                            <div className="h-1 bg-gray-200 overflow-hidden">
                                <div
                                    className="h-full bg-green-600 transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        )}

                        {/* Content */}
                        <div
                            ref={contentContainerRef}
                            className={`w-full flex flex-col gap-2 overflow-auto flex-1 px-6 pt-6 text-base text-content-strong ${
                                hasScroll ? 'border-b border-line-weak pb-6' : ''
                            }`}
                        >
                            {children}
                        </div>

                        {/* Footer */}
                        {onConfirm && (
                            <div className="flex gap-2 justify-end p-6 rounded-b-xl">
                                <Button
                                    loading={loading}
                                    disabled={confirmDisabled}
                                    onClick={onConfirm}
                                    type="submit"
                                    variant="primary"
                                >
                                    {loading && (
                                        <LoadingIcon className="text-content-brand-strong text-sm animate-spin mr-2" />
                                    )}
                                    {confirmText}
                                    {confirmIcon}
                                </Button>
                            </div>
                        )}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default ProgressModal;
